import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/shared/auth';
import { Card } from '@/shared/components/ui/Card';
import { useI18n } from '@/shared/i18n/useT';
import { isAdmin, useOrg } from '@/shared/org';
import {
  checkSlug,
  normalizeSlug,
  parseSlugRejection,
  type SlugProblem,
  type SlugRejection,
} from '@/shared/org/publicSlug';

interface RequestRow {
  id: string;
  requested_slug: string;
  status: 'pending' | 'approved' | 'rejected';
  reason: string | null;
}

/**
 * Ask for numtable.com/<business>.
 *
 * The address is requested, never taken: submitting queues a row that I read
 * and decide on, and only an approval assigns it. That is a product decision
 * rather than a technical one — a reserved-word list cannot recognise an
 * offensive name, a competitor's trademark, or a near-miss on our own brand —
 * so the copy has to be honest about the wait instead of implying a form that
 * hands out URLs.
 *
 * Nothing here is a security boundary. `checkSlug` runs so the field can
 * explain itself while someone types; the database refuses the same names again
 * through `reserved_slugs` and its triggers, and that refusal is the one that
 * counts.
 */
export function OrgAddressCard() {
  const { t } = useI18n();
  const org = useOrg();
  const session = useSession();
  const fieldId = useId();

  const [value, setValue] = useState('');
  const [request, setRequest] = useState<RequestRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rejection, setRejection] = useState<SlugRejection | 'unknown' | null>(null);

  const orgId = org.status === 'ready' ? org.orgId : null;
  const assigned = org.status === 'ready' ? org.membership.organization.publicSlug : null;
  const editable = org.status === 'ready' && !org.isViewingAs && isAdmin(org.role);
  const userId = session.session?.user.id ?? null;

  const load = useCallback(async () => {
    if (!orgId) return;
    // Newest first: an org may have been refused before, and the row that
    // matters is the last decision, not the first attempt.
    const { data } = await supabase
      .from('org_slug_requests')
      .select('id, requested_slug, status, reason')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(1);

    setRequest((data?.[0] as RequestRow | undefined) ?? null);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const candidate = normalizeSlug(value);
  const problem: SlugProblem | null = value.trim() === '' ? null : checkSlug(candidate);
  const pending = request?.status === 'pending';
  const canSubmit = editable && !pending && !assigned && !saving && candidate !== '' && problem === null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || !orgId) return;

    setSaving(true);
    setRejection(null);

    const { error } = await supabase.from('org_slug_requests').insert({
      org_id: orgId,
      requested_slug: candidate,
      // The INSERT policy checks this against `auth.uid()`; it records who
      // asked and grants nothing.
      requested_by: userId,
    });

    if (error) {
      setRejection(parseSlugRejection(error) ?? 'unknown');
      setSaving(false);
      return;
    }

    setValue('');
    setSaving(false);
    await load();
  }

  if (org.status !== 'ready') return null;

  return (
    <Card title={t('settings.org.address.title')} description={t('settings.org.address.description')}>
      {loading ? (
        <p className="text-sm text-muted">{t('common.loading')}</p>
      ) : assigned ? (
        <div className="flex flex-col gap-1">
          <p className="break-words text-sm font-medium text-fg">numtable.com/{assigned}</p>
          <p className="text-sm text-muted">{t('settings.org.address.assigned')}</p>
        </div>
      ) : pending ? (
        <div className="flex flex-col gap-1">
          <p className="break-words text-sm font-medium text-fg">
            numtable.com/{request?.requested_slug}
          </p>
          <p className="text-sm text-muted">{t('settings.org.address.pending')}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3">
          {request?.status === 'rejected' && (
            <p className="rounded-token border border-border bg-surface-3 p-3 text-sm text-muted">
              {t('settings.org.address.rejected', { slug: request.requested_slug })}
              {request.reason && <span className="mt-1 block break-words">{request.reason}</span>}
            </p>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <label htmlFor={fieldId} className="mb-1 block text-sm text-muted">
                {t('settings.org.address.label')}
              </label>
              <div className="flex min-w-0 items-center gap-1">
                <span className="shrink-0 text-sm text-muted">numtable.com/</span>
                <input
                  id={fieldId}
                  value={value}
                  disabled={!editable}
                  onChange={(event) => {
                    setValue(event.target.value);
                    setRejection(null);
                  }}
                  maxLength={40}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-describedby={`${fieldId}-hint`}
                  className="tap-target min-w-0 flex-1 rounded-full border border-border-strong bg-surface px-3 text-sm text-fg disabled:opacity-60"
                />
              </div>
              {/* The normalized form, so nobody is surprised by what they get:
                  "Estudio Pérez" is submitted as `estudio-perez`. */}
              <p id={`${fieldId}-hint`} className="mt-1 break-words text-xs text-muted">
                {problem
                  ? t(`settings.org.address.problem.${problem}`)
                  : candidate !== '' && candidate !== value
                    ? t('settings.org.address.willAskFor', { slug: candidate })
                    : t('settings.org.address.hint')}
              </p>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="tap-target shrink-0 rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-50"
            >
              {saving ? t('settings.org.address.submitting') : t('settings.org.address.submit')}
            </button>
          </div>

          {rejection && (
            <p role="alert" className="text-sm text-danger">
              {t(
                rejection === 'unknown'
                  ? 'settings.org.address.error'
                  : `settings.org.address.rejection.${rejection}`,
              )}
            </p>
          )}

          {!editable && <p className="text-sm text-muted">{t('settings.org.readOnly')}</p>}
        </form>
      )}
    </Card>
  );
}
